import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, AttributionControl, useMap } from "react-leaflet";
import { ArrowLeft, Car, MapPin, Phone, Clock, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import { DARK_TILES, TILE_ATTRIB, vehicleIcon, dotIcon } from "@/lib/mapUtils";

const FLOW = ["confirmed", "assigned", "arrived", "on_board", "completed"];
const ROUTE_STYLE = { color: "#0A6BB5", weight: 4, opacity: 0.85 };
const ORIGIN_ICON = dotIcon("#10B981", "A");
const DEST_ICON = dotIcon("#F43F5E", "B");
// Map every live backend status onto a canonical timeline step.
const STATUS_STEP = {
  confirmed: 0,
  assigned: 1,
  driver_on_way: 1,
  en_route: 1,
  arrived: 2,
  on_board: 3,
  completed: 4,
};

// Keep all trip points in view.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    const pts = points.filter(Boolean);
    if (pts.length === 1) map.setView(pts[0], 12);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [50, 50], maxZoom: 13 });
  }, [points, map]);
  return null;
}

export default function TripLive() {
  const { code } = useParams();
  const { t } = useI18n();
  const [booking, setBooking] = useState(null);
  const [loc, setLoc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const timerRef = useRef(null);

  const loadBooking = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/code/${code}`);
      setBooking(data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(apiError(e));
    }
  }, [code]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  useEffect(() => {
    if (!booking) return;
    const trackable = ["assigned", "driver_on_way", "en_route", "arrived", "on_board"].includes(booking.status);
    if (!trackable) {
      setLoc(null);
      return;
    }
    const poll = () =>
      api
        .get(`/bookings/${booking.booking_id}/driver-location`)
        .then(({ data }) => setLoc(data))
        .catch(() => setLoc(null));
    poll();
    timerRef.current = setInterval(() => {
      poll();
      loadBooking();
    }, 15000);
    return () => clearInterval(timerRef.current);
  }, [booking, loadBooking]);

  if (notFound)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-3xl px-5 py-24">
          <p className="text-slate-600" data-testid="trip-not-found">{t("live.notFound")}</p>
          <Link to="/bookings" className="mt-4 inline-block text-sm font-semibold text-sea underline">
            {t("bookings.back")}
          </Link>
        </div>
      </div>
    );

  if (!booking)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <p className="mx-auto max-w-3xl px-5 py-24 text-slate-500" data-testid="trip-loading">
          {t("common.loading")}
        </p>
      </div>
    );

  const origin = booking.origin?.lat ? [booking.origin.lat, booking.origin.lng] : null;
  const dest = booking.destination?.lat ? [booking.destination.lat, booking.destination.lng] : null;
  const driver = loc?.latitude ? [loc.latitude, loc.longitude] : null;
  // Route line: origin → driver (if live) → destination.
  const routeLine = [origin, driver, dest].filter(Boolean);
  const stepIndex = STATUS_STEP[booking.status] ?? -1;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-[1500px] px-4 py-6 lg:px-8">
        <Link
          to={`/bookings`}
          data-testid="trip-back-btn"
          className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors duration-200 hover:text-sea"
        >
          <ArrowLeft size={15} /> {t("bookings.back")}
        </Link>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{t("live.tripTitle")} · {booking.code}</p>
            <h1 className="mt-2 flex items-center gap-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="trip-title">
              <Navigation size={22} className="text-sea" />
              {booking.origin?.name} → {booking.destination?.name}
            </h1>
          </div>
          <StatusBadge status={booking.status} testId="trip-status" />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-12">
          {/* Map */}
          <div className="vaya-map overflow-hidden rounded-2xl border border-slate-800 lg:col-span-8">
            <div className="h-[55vh] lg:h-[70vh]" data-testid="trip-map">
              <MapContainer center={origin || [36.85, 31.0]} zoom={10} scrollWheelZoom attributionControl={false} style={{ height: "100%", width: "100%" }}>
                <AttributionControl prefix={false} />
                <TileLayer url={DARK_TILES} attribution={TILE_ATTRIB} />
                <FitBounds points={[origin, dest, driver]} />
                {routeLine.length > 1 && (
                  <Polyline positions={routeLine} pathOptions={{ ...ROUTE_STYLE, dashArray: driver ? null : "8 10" }} />
                )}
                {origin && (
                  <Marker position={origin} icon={ORIGIN_ICON}>
                    <Popup><span className="text-xs text-white">{booking.origin?.name}</span></Popup>
                  </Marker>
                )}
                {dest && (
                  <Marker position={dest} icon={DEST_ICON}>
                    <Popup><span className="text-xs text-white">{booking.destination?.name}</span></Popup>
                  </Marker>
                )}
                {driver && (
                  <Marker position={driver} icon={vehicleIcon(booking.status, false)}>
                    <Popup>
                      <div className="text-xs">
                        <p className="font-semibold text-white">{booking.driver?.name || t("bookings.driver")}</p>
                        {booking.driver?.vehicle && <p className="text-slate-300">{booking.driver.vehicle}</p>}
                        {loc?.eta_min ? <p className="mt-1 text-sea">{loc.eta_min} {t("results.min")} · {loc.eta_km} {t("results.km")}</p> : null}
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>
          </div>

          {/* Info panel */}
          <aside className="space-y-5 lg:col-span-4">
            <section className="rounded-xl border border-slate-200 bg-white p-6 ambient">
              <p className="label-eyebrow">{t("bookings.track")}</p>
              {driver ? (
                <div className="mt-4 space-y-2 text-sm" data-testid="trip-live-eta">
                  <p className="inline-flex items-center gap-2 font-medium text-slate-900">
                    <Car size={15} className="text-sea" /> {t("live.driverPosition")}
                  </p>
                  {loc?.eta_min ? (
                    <p className="inline-flex items-center gap-2 text-slate-700">
                      <Clock size={14} className="text-sea" /> {loc.eta_min} {t("results.min")} · {loc.eta_km} {t("results.km")}
                    </p>
                  ) : null}
                  <p className="text-xs text-slate-400">
                    {t("live.updated")}: {loc?.recorded_at ? new Date(loc.recorded_at).toLocaleTimeString() : "—"}
                  </p>
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500" data-testid="trip-no-location">{t("bookings.noLocation")}</p>
              )}
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-6 ambient">
              <p className="label-eyebrow">{t("bookings.timeline")}</p>
              <ol className="mt-5 space-y-4" data-testid="trip-timeline">
                {FLOW.map((s, i) => (
                  <li key={s} className="flex items-center gap-3">
                    <span
                      className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold ${
                        booking.status === "cancelled"
                          ? "bg-slate-100 text-slate-400"
                          : i <= stepIndex
                            ? "bg-sea text-white"
                            : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className={`text-sm ${i <= stepIndex && booking.status !== "cancelled" ? "font-medium text-slate-900" : "text-slate-400"}`}>
                      {t(`status.${s}`)}
                    </span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-6 ambient">
              <p className="label-eyebrow">{t("bookings.driver")}</p>
              {booking.driver ? (
                <div className="mt-4 space-y-2 text-sm" data-testid="trip-driver">
                  <p className="font-medium text-slate-900">{booking.driver.name}</p>
                  {booking.driver.vehicle && (
                    <p className="text-slate-600">{booking.driver.vehicle}{booking.driver.plate ? ` · ${booking.driver.plate}` : ""}</p>
                  )}
                  {booking.driver.phone && (
                    <a href={`tel:${booking.driver.phone}`} className="inline-flex items-center gap-2 text-sea underline">
                      <Phone size={13} /> {booking.driver.phone}
                    </a>
                  )}
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">{t("bookings.noDriver")}</p>
              )}
              <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <MapPin size={13} className="text-sea" /> PIN {booking.pin}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
